import { api } from './api';
import {
  InsightsPlatform, InsightsSection, InsightsSectionMap, InsightsSummary, PullAccepted, StoreFilter, StoreTapStatsResponse,
} from '../types/marketing';

/**
 * Spec 078 — store-tap numbers for the Marketing page, served by **platform** (gateway route
 * `platform-admin-marketing`, admin screen `marketing`). Errors: 400 bad range, 403 no screen,
 * 503 `{"error":"STATS_UNAVAILABLE"}` (the page shows "Numbers unavailable, try again").
 */
export function getStoreTaps(
  token: string,
  from: string,
  to: string,
  store?: StoreFilter,
  signal?: AbortSignal,
): Promise<StoreTapStatsResponse> {
  const qs = new URLSearchParams({ from, to });
  if (store) qs.set('store', store);
  const url = `/api/admin/marketing/store-taps?${qs.toString()}`;
  return signal ? api.getAbortable<StoreTapStatsResponse>(url, token, signal) : api.get<StoreTapStatsResponse>(url, token);
}

function insightsQuery(from: string, to: string, platform: InsightsPlatform, campaignKey?: string): string {
  const qs = new URLSearchParams({ from, to, platform });
  if (campaignKey) qs.set('campaignKey', campaignKey);
  return qs.toString();
}

/**
 * Spec 084 — funnel, daily series, campaigns, blended cost and the per-source data stamps.
 * Errors: 400 `BAD_RANGE`, 403 no screen, **404 `FEATURE_OFF`** (switch `garagat.marketing.insights.enabled`
 * off: the page shows the Spec 078 view), 503 `STATS_UNAVAILABLE` (no source has an OK pull in the range).
 */
export function getInsights(
  token: string,
  from: string,
  to: string,
  platform: InsightsPlatform,
  signal: AbortSignal,
): Promise<InsightsSummary> {
  return api.getAbortable<InsightsSummary>(`/api/admin/marketing/insights?${insightsQuery(from, to, platform)}`, token, signal);
}

/** Spec 084 — one tab's numbers (`ads`, `channels`, `supply`, `retention`, `alerts`); same errors as `getInsights`. */
export function getInsightsSection<S extends InsightsSection>(
  token: string,
  section: S,
  from: string,
  to: string,
  platform: InsightsPlatform,
  signal: AbortSignal,
  campaignKey?: string,
): Promise<InsightsSectionMap[S]> {
  const url = `/api/admin/marketing/insights/${section}?${insightsQuery(from, to, platform, campaignKey)}`;
  return api.getAbortable<InsightsSectionMap[S]>(url, token, signal);
}

/** Spec 084 — super admin: re-run the nightly pull for one Dubai day. 202 accepted; 409 `RUN_IN_PROGRESS`. */
export function triggerPull(token: string, day?: string): Promise<PullAccepted> {
  const qs = day ? `?${new URLSearchParams({ day }).toString()}` : '';
  return api.post<PullAccepted>(`/api/admin/marketing/insights/pull${qs}`, undefined, token);
}
